import React from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import AddProvidersToRosterPage from './AddProvidersToRosterPage/AddProvidersToRosterPage';
import { AUTH_CATEGORIES, AUTH_STATUS, AuthProvider, useAuth } from './Auth';
import CheckoutPage from './CheckoutPage/CheckoutPage';
import ElevatedLandingPage from './ElevatedLandingPage/ElevatedLandingPage';
import ErrorComponent from './Error/Error';
import FeatureFlagsProvider, {
  useFeatureFlags,
} from './FeatureFlag/FeatureFlagsProvider';
import Footer from './Footer/Footer';
import Header from './Header/Header';
import Spinner from './Spinner/Spinner';
import TermsOfService from './TermsOfService/TermsOfService';

/** @typedef {import('./Auth').UserCategory} UserCategory */

export const ROUTES = Object.freeze({
  LANDING_PAGE: '/',
  ADD_PROVIDERS_TO_ROSTER_PAGE: '/add-providers-to-roster',
  CHECKOUT_PAGE: '/checkout',
  TERMS_PAGE: '/terms',
});

/**
 * Handles authentication-based routing for root route
 * - ?policy=terms-of-service → show terms page
 * - Unauthed → show landing
 * - Authed   → redirect by role
 * - Unsupported/Unknown → show landing
 */
const LandingRouteGuard = () => {
  const { status, userCategory } = useAuth();
  const [searchParams] = useSearchParams();

  // Check for policy query parameter - show terms page if present
  const policy = searchParams.get('policy');
  if (policy === 'terms-of-service') {
    return <TermsOfService />;
  }

  if (status === AUTH_STATUS.LOADING) {
    return <Spinner />;
  }

  if (status === AUTH_STATUS.UNAUTHENTICATED) {
    return <ElevatedLandingPage />;
  }

  // If authenticated and user is a provider, redirect to CheckoutPage
  if (userCategory === AUTH_CATEGORIES.PROVIDER) {
    return <Navigate to={ROUTES.CHECKOUT_PAGE} replace />;
  }

  // If authenticated and user is an admin, redirect to AddProvidersToRosterPage
  if (userCategory === AUTH_CATEGORIES.ADMIN) {
    return <Navigate to={ROUTES.ADD_PROVIDERS_TO_ROSTER_PAGE} replace />;
  }

  // UNSUPPORTED, UNKNOWN, or any other unhandled category
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `🔐 Unhandled user category: "${userCategory}" - showing landing page.`,
    );
  }
  return <ElevatedLandingPage />;
};

/**
 * Protected route wrapper that enforces authentication and role-based access control.
 * @param {{
 *   children: React.ReactNode,
 *   allowedUserCategories?: UserCategory[],
 *   redirectTo?: string
 * }} props
 */
const AuthenticatedRoute = ({
  children,
  allowedUserCategories,
  redirectTo = ROUTES.LANDING_PAGE,
}) => {
  const { status, userCategory } = useAuth();
  const location = useLocation();

  const { DisableAuth } = useFeatureFlags();

  if (status === AUTH_STATUS.LOADING) {
    return <Spinner />;
  }

  const allowed = Array.isArray(allowedUserCategories)
    ? allowedUserCategories
    : [];
  const isAllowedCategory =
    // Empty allowedUserCategories means "allow all authenticated users"
    allowed.length === 0 || allowed.includes(userCategory);
  const userHasAccess =
    status === AUTH_STATUS.AUTHENTICATED && isAllowedCategory;

  if (userHasAccess) {
    return children;
  }

  if (process.env.NODE_ENV === 'development') {
    if (DisableAuth) {
      // Dev bypass for local testing/storybook
      console.warn(
        `🔐⚠️ Auth disabled: allowing access to ${location.pathname}`,
      );
      return children;
    }

    const reason =
      status === AUTH_STATUS.UNAUTHENTICATED
        ? 'Not authenticated'
        : userCategory === AUTH_CATEGORIES.UNSUPPORTED
          ? `UNSUPPORTED user category`
          : userCategory === AUTH_CATEGORIES.UNKNOWN
            ? `UNKNOWN user category`
            : `Category "${userCategory}" not in allowed list`;

    console.groupCollapsed(
      `🔐⛔ Access denied • ${location.pathname} • redirect → ${redirectTo}`,
    );
    console.log('Reason:', reason);
    console.log('Auth status:', status);
    console.log('User category:', userCategory);
    console.log('Allowed categories:', allowed.length ? allowed : 'none');
    console.groupEnd();
  }

  return <Navigate to={redirectTo} state={{ from: location }} replace />;
};

const AppRouter = () => (
  <Routes>
    <Route
      path={ROUTES.ADD_PROVIDERS_TO_ROSTER_PAGE}
      element={
        <AuthenticatedRoute allowedUserCategories={[AUTH_CATEGORIES.ADMIN]}>
          <AddProvidersToRosterPage />
        </AuthenticatedRoute>
      }
    />

    <Route
      path={ROUTES.CHECKOUT_PAGE}
      element={
        <AuthenticatedRoute
          allowedUserCategories={[
            AUTH_CATEGORIES.ADMIN,
            AUTH_CATEGORIES.PROVIDER,
          ]}
        >
          <CheckoutPage />
        </AuthenticatedRoute>
      }
    />

    <Route path={ROUTES.LANDING_PAGE} element={<LandingRouteGuard />} />

    <Route path={ROUTES.TERMS_PAGE} element={<TermsOfService />} />

    {/* v6 catch-all: use "*" path */}
    <Route path="*" element={<ErrorComponent />} />
  </Routes>
);

const App = () => (
  <FeatureFlagsProvider>
    <AuthProvider>
      <Header />
      <div className="App">
        <AppRouter />
      </div>
      <Footer />
    </AuthProvider>
  </FeatureFlagsProvider>
);

export default App;
